import React, { useMemo, useState } from 'react';
import { useProvider } from '../../contexts/ProviderContext';
import { useProviderDemand } from '../../hooks/useProviderDemand';
import ProviderLayout from './ProviderLayout';
import { ModelRow, RangePicker } from '../analytics/ModelRow';
import { formatMoney, RANGES, type RangeDays, type SupplierStatus } from '../../lib/analytics';

/**
 * Supplier analytics: how your offers did, and which hot models you could be stocking.
 * Network demand is a rank and a level; competitors are never named and their prices never
 * shown — only how far above the winning price you were on average.
 */

const LIST_SHOWN = 15;

const Tile: React.FC<{ label: string; value: string; hint?: string }> = ({ label, value, hint }) => (
  <div className="glass-card rounded-2xl p-5">
    <p className="text-xs text-white/50 mb-2">{label}</p>
    <p className="font-heading font-bold text-white text-2xl tabular-nums">{value}</p>
    {hint && <p className="text-xs text-white/50 mt-1">{hint}</p>}
  </div>
);

const FILTERS: Array<{ key: 'gaps' | SupplierStatus; label: string }> = [
  { key: 'gaps', label: 'All gaps' },
  { key: 'out_of_stock', label: 'Out of stock' },
  { key: 'not_carried', label: 'Not in your catalogue' },
  { key: 'in_stock', label: 'You have it' },
];

const ProviderAnalytics: React.FC = () => {
  const { currentProviderId } = useProvider();
  const [days, setDays] = useState<RangeDays>(30);
  const [filter, setFilter] = useState<'gaps' | SupplierStatus>('gaps');
  const [showAll, setShowAll] = useState(false);
  const { data, error } = useProviderDemand(currentProviderId, days);

  const models = useMemo(
    () => (data?.models ?? []).filter((m) => (filter === 'gaps' ? m.status !== 'in_stock' : m.status === filter)),
    [data, filter],
  );
  const missed = (data?.results ?? []).filter((r) => r.missed > 0);
  const s = data?.summary;

  return (
    <ProviderLayout>
      <div className="max-w-5xl space-y-6">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="font-heading font-bold text-white text-3xl">Analytics</h1>
            <p className="text-white/50 text-sm mt-1">How your offers did, and the demand you could capture.</p>
          </div>
          <RangePicker value={days} options={RANGES} onChange={(d: RangeDays) => { setDays(d); setShowAll(false); }} />
        </div>

        {error && (
          <div className="rounded-xl border border-red-500/30 bg-red-500/10 text-red-300 text-sm px-4 py-3">{error}</div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Tile label="Orders won" value={s ? String(s.won) : '…'} hint={`Last ${days} days`} />
          <Tile
            label="Missed on price"
            value={s ? String(s.lost_on_price) : '…'}
            hint={s?.avg_gap ? `On average ${formatMoney(s.avg_gap, 'EUR', 2)} above the winning price` : 'You had stock; another offer was cheaper'}
          />
          <Tile label="Missed on stock" value={s ? String(s.lost_no_stock) : '…'} hint="Ordered while you were out of stock" />
        </div>

        <section className="glass-card rounded-2xl p-6">
          <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
            <div>
              <h2 className="font-heading font-bold text-white text-lg">Demand you could capture</h2>
              <p className="text-sm text-white/50 mt-0.5">
                The network’s top-selling models in the last {days} days, with the sizes that sell most.
              </p>
            </div>
            <div className="flex flex-wrap gap-2" role="group" aria-label="Filter">
              {FILTERS.map((f) => (
                <button
                  key={f.key}
                  onClick={() => { setFilter(f.key); setShowAll(false); }}
                  aria-pressed={filter === f.key}
                  className={`rounded-full px-3 py-1 text-xs transition-colors border ${
                    filter === f.key ? 'bg-white/[0.12] border-white/20 text-white' : 'border-white/10 text-white/60 hover:bg-white/[0.06]'
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </div>

          {!data ? (
            <div className="space-y-3 py-2" aria-busy="true">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="h-12 rounded-lg bg-white/[0.04] animate-pulse" />
              ))}
            </div>
          ) : models.length === 0 ? (
            <p className="text-sm text-white/50 py-4">
              {filter === 'gaps' ? 'You have every hot model in stock.' : 'Nothing in this group for the period.'}
            </p>
          ) : (
            <>
              <ul>
                {(showAll ? models : models.slice(0, LIST_SHOWN)).map((m) => (
                  <ModelRow
                    key={m.sku}
                    rank={m.rank}
                    sku={m.sku}
                    title={m.title}
                    brand={m.brand}
                    photoUrl={m.photo_url}
                    demand={m.demand}
                    trend={m.trend}
                    hasActions={false}
                    note={
                      <>
                        {m.status === 'in_stock'
                          ? 'In stock with you'
                          : m.status === 'out_of_stock'
                            ? 'In your catalogue, out of stock'
                            : 'Not in your catalogue'}
                        {m.top_sizes.length > 0 && ` · sizes selling most: ${m.top_sizes.join(', ')}`}
                      </>
                    }
                  />
                ))}
              </ul>
              {models.length > LIST_SHOWN && (
                <button onClick={() => setShowAll((v) => !v)} className="text-xs text-orange-400 hover:text-orange-300 mt-2">
                  {showAll ? 'Show fewer' : `Show all ${models.length}`}
                </button>
              )}
            </>
          )}
        </section>

        <section className="glass-card rounded-2xl p-6">
          <h2 className="font-heading font-bold text-white text-lg">Where you missed orders</h2>
          <p className="text-sm text-white/50 mt-0.5 mb-3">
            Orders from stores you are connected to, where your offer was in the running and another one won.
          </p>
          {!data ? (
            <div className="h-24 rounded-lg bg-white/[0.04] animate-pulse" aria-busy="true" />
          ) : missed.length === 0 ? (
            <p className="text-sm text-white/50 py-2">You won every order you were in the running for.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-left">
                <thead className="text-xs text-white/40">
                  <tr>
                    <th className="py-2 pr-4 font-medium">Model</th>
                    <th className="py-2 pr-4 font-medium text-right">Won</th>
                    <th className="py-2 pr-4 font-medium text-right">Missed on price</th>
                    <th className="py-2 pr-4 font-medium text-right">Avg. above winner</th>
                    <th className="py-2 font-medium text-right">Missed on stock</th>
                  </tr>
                </thead>
                <tbody>
                  {missed.slice(0, 30).map((r) => (
                    <tr key={r.sku} className="border-t border-white/[0.06]">
                      <td className="py-2 pr-4">
                        <span className="text-white">{r.title || r.sku}</span>
                        {r.title && <span className="text-white/40 text-xs block">{r.sku}</span>}
                      </td>
                      <td className="py-2 pr-4 text-right tabular-nums text-white/80">{r.won}</td>
                      <td className="py-2 pr-4 text-right tabular-nums text-white/80">{r.lost_on_price}</td>
                      <td className="py-2 pr-4 text-right tabular-nums text-white/80">
                        {r.avg_gap ? formatMoney(r.avg_gap, 'EUR', 2) : '—'}
                      </td>
                      <td className="py-2 text-right tabular-nums text-white/80">{r.lost_no_stock}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <p className="text-xs text-white/35">
          Network demand is shown as a rank and a demand level. No store’s sales figures and no other supplier’s name or
          price are shown.
        </p>
      </div>
    </ProviderLayout>
  );
};

export default ProviderAnalytics;
